// src/hooks/useBulkQRGenerator.js - FIXED DUPLICATE ISSUE
import { useState } from 'react';

const useBulkQRGenerator = (planTracker, userId) => {
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  const [bulkError, setBulkError] = useState('');
  const [extractedItems, setExtractedItems] = useState([]);
  
  // Chunking state
  const [pdfChunks, setPdfChunks] = useState([]);
  const [currentChunkIndex, setCurrentChunkIndex] = useState(0);
  const [allExtractedItems, setAllExtractedItems] = useState([]);
  const [isChunkedMode, setIsChunkedMode] = useState(false);

  // Token estimation: ~4 characters per token
  const CHARS_PER_TOKEN = 4;
  const MAX_SAFE_TOKENS = 100000; // Leave room for system prompt + response
  const CHUNK_SIZE_TOKENS = 80000; // Safe chunk size
  const CHUNK_SIZE_CHARS = CHUNK_SIZE_TOKENS * CHARS_PER_TOKEN; // 320,000 chars

  /**
   * Estimate token count from text
   */
  const estimateTokens = (text) => {
    return Math.ceil(text.length / CHARS_PER_TOKEN);
  };

  /**
   * Split large PDF text into manageable chunks
   */
  const chunkPDFText = (pdfText) => {
    const estimatedTokens = estimateTokens(pdfText);
    
    console.log(`PDF text: ${pdfText.length} chars, ~${estimatedTokens} tokens`);
    
    // If small enough, no chunking needed
    if (estimatedTokens <= MAX_SAFE_TOKENS) {
      return [pdfText];
    }

    // Split into chunks
    const chunks = [];
    let currentPos = 0;
    
    while (currentPos < pdfText.length) {
      // Extract chunk
      let chunkEnd = Math.min(currentPos + CHUNK_SIZE_CHARS, pdfText.length);
      
      // Try to break at a natural boundary (paragraph, sentence, or word)
      if (chunkEnd < pdfText.length) {
        // Look for paragraph break
        const paragraphBreak = pdfText.lastIndexOf('\n\n', chunkEnd);
        if (paragraphBreak > currentPos + (CHUNK_SIZE_CHARS * 0.8)) {
          chunkEnd = paragraphBreak + 2;
        } else {
          // Look for sentence break
          const sentenceBreak = pdfText.lastIndexOf('. ', chunkEnd);
          if (sentenceBreak > currentPos + (CHUNK_SIZE_CHARS * 0.8)) {
            chunkEnd = sentenceBreak + 2;
          } else {
            // Look for word break
            const wordBreak = pdfText.lastIndexOf(' ', chunkEnd);
            if (wordBreak > currentPos + (CHUNK_SIZE_CHARS * 0.8)) {
              chunkEnd = wordBreak + 1;
            }
          }
        }
      }
      
      const chunk = pdfText.substring(currentPos, chunkEnd).trim();
      chunks.push(chunk);
      
      console.log(`Chunk ${chunks.length}: ${chunk.length} chars, ~${estimateTokens(chunk)} tokens`);
      
      currentPos = chunkEnd;
    }
    
    console.log(`Split into ${chunks.length} chunks`);
    return chunks;
  };

  /**
   * Extract QR items from PDF text using AI
   */
  const extractQRItemsFromPDF = async (pdfText, chunkInfo = null) => {
    const apiKey = process.env.REACT_APP_GROQ_API_KEY;
    if (!apiKey) {
      throw new Error('Groq API key is missing');
    }

    // Build context-aware prompt
    let contextPrompt = '';
    if (chunkInfo) {
      contextPrompt = `\n\nIMPORTANT: This is chunk ${chunkInfo.current} of ${chunkInfo.total} from a larger document. Extract ALL items from this section. Previous chunks have already been processed. DO NOT duplicate items from previous chunks.`;
    }

    const systemPrompt = `You are an expert at analyzing documents and extracting QR code information. 

Analyze the provided PDF text and extract ALL items that should become individual QR codes.

RETURN ONLY A VALID JSON ARRAY with this exact structure:
[
  {
    "type": "url|vcard|wifi|email|phone|sms|event|location|text",
    "content": "properly formatted QR content",
    "description": "brief description of this item"
  }
]

IMPORTANT FORMATS:
- VCARD: BEGIN:VCARD\\nVERSION:3.0\\nFN:Name\\nTEL:+1234567890\\nEMAIL:email@example.com\\nADR:;;Street;City;State;ZIP;Country\\nEND:VCARD
- WIFI: WIFI:T:WPA;S:NetworkName;P:Password;;
- URL: Full URL with https://
- EMAIL: mailto:email@example.com
- PHONE: tel:+1234567890
- SMS: smsto:+1234567890:Message
- EVENT: BEGIN:VEVENT\\nSUMMARY:Title\\nDTSTART:20240101T120000Z\\nEND:VEVENT
- LOCATION: geo:latitude,longitude

⚠️ CRITICAL VCARD RULES - READ CAREFULLY:
1. Create ONE vCard per UNIQUE person + location combination
2. If "John Doe" has a home address → 1 vCard with home address inside
3. If "John Doe" has a work address → 1 SEPARATE vCard with work address inside
4. INCLUDE the address INSIDE the vCard using the ADR field
5. DO NOT create separate location QR codes for addresses that belong to a person
6. A person's address should be PART OF their vCard, not a separate item

EXAMPLE - CORRECT:
Input: "John Doe, home: 123 Main St, work: 456 Oak Ave"
Output: [
  {
    "type": "vcard",
    "content": "BEGIN:VCARD\\nVERSION:3.0\\nFN:John Doe\\nADR:;;123 Main St;;;;\\nNOTE:Home\\nEND:VCARD",
    "description": "John Doe - Home Contact"
  },
  {
    "type": "vcard",
    "content": "BEGIN:VCARD\\nVERSION:3.0\\nFN:John Doe\\nADR:;;456 Oak Ave;;;;\\nNOTE:Work\\nEND:VCARD",
    "description": "John Doe - Work Contact"
  }
]

EXAMPLE - WRONG (DO NOT DO THIS):
❌ Creating "John Doe" vCard AND separate "John Doe's address" location
❌ Creating duplicate entries for the same person

OTHER EXTRACTION RULES:
1. Phone numbers not tied to a person → "phone" type QR codes
2. Email addresses not tied to a person → "email" type QR codes  
3. URLs/links → "url" type QR codes
4. WiFi credentials → "wifi" type QR codes
5. Event details → "event" type QR codes
6. Standalone addresses (not belonging to a person) → "location" type QR codes
7. Any other important text → "text" type QR codes

If the document is a contact list, create one vCard per contact per location.
If the document has multiple URLs, create one QR per URL.
If the document is a form/contract, extract key information as separate QR codes.${contextPrompt}

Return ONLY the JSON array, no explanations, no markdown, no code blocks.`;

    try {
      const response = await fetch('https://api.groq.com/openai/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: "llama-3.3-70b-versatile",
          messages: [
            {
              role: "system",
              content: systemPrompt
            },
            {
              role: "user",
              content: `Extract all QR code items from this document:\n\n${pdfText}`
            }
          ],
          temperature: 0.3,
          max_tokens: 4000
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to extract QR items from PDF');
      }

      const data = await response.json();
      let aiResponse = data.choices[0]?.message?.content?.trim();

      if (!aiResponse) {
        throw new Error('No response from AI');
      }

      // Clean up response
      aiResponse = aiResponse
        .replace(/```json\n?/g, '')
        .replace(/```\n?/g, '')
        .trim();

      // Parse JSON array
      let items;
      try {
        items = JSON.parse(aiResponse);
      } catch (parseError) {
        console.error('Failed to parse AI response:', aiResponse);
        throw new Error('AI returned invalid format. Please try again.');
      }

      // Validate array
      if (!Array.isArray(items)) {
        throw new Error('AI response is not an array');
      }

      if (items.length === 0) {
        console.warn('No items extracted from this chunk');
        return [];
      }

      // Validate each item
      const validatedItems = items.filter(item => {
        return item.type && item.content && item.description;
      });

      console.log(`Successfully extracted ${validatedItems.length} QR items from chunk`);
      return validatedItems;

    } catch (error) {
      console.error('Error extracting QR items:', error);
      throw error;
    }
  };

  /**
   * Generate QR codes in bulk from extracted items WITHOUT adding to state
   * Returns the QR codes for manual state management
   */
  const generateBulkQRCodes = async (items, generateQRFromPrompt, isDynamic = false) => {
    setIsBulkProcessing(true);
    setBulkError('');
    setBulkProgress({ current: 0, total: items.length });

    const generatedQRs = [];
    const errors = [];

    try {
      // Generate QR codes one by one
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        
        setBulkProgress({ current: i + 1, total: items.length });

        try {
          const prompt = `${item.description}: ${item.content}`;
          
          // CRITICAL FIX: Call generateQRFromPrompt but with a flag to NOT add to state
          // We need to get the QR object WITHOUT it being added to the global state
          const qr = await generateQRFromPrompt(prompt, isDynamic, true); // Third param: skipStateUpdate
          
          const qrWithBulkInfo = {
            ...qr,
            bulkIndex: i,
            bulkDescription: item.description,
            bulkType: item.type
          };
          
          generatedQRs.push(qrWithBulkInfo);

          // Small delay to avoid rate limiting
          if (i < items.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }

        } catch (error) {
          console.error(`Error generating QR ${i + 1}:`, error);
          errors.push({
            index: i,
            description: item.description,
            error: error.message
          });
        }
      }

      // Show summary
      if (errors.length > 0) {
        const errorMessage = `Generated ${generatedQRs.length}/${items.length} QR codes. ${errors.length} failed.`;
        setBulkError(errorMessage);
        console.warn('Bulk generation errors:', errors);
      }

      return generatedQRs;

    } catch (error) {
      setBulkError(error.message);
      throw error;
    } finally {
      setIsBulkProcessing(false);
      setBulkProgress({ current: 0, total: 0 });
    }
  };

  /**
   * Process a single chunk of PDF text
   */
  const processChunk = async (chunkText, chunkIndex, totalChunks, generateQRFromPrompt, isDynamic) => {
    console.log(`Processing chunk ${chunkIndex + 1}/${totalChunks}...`);
    
    const chunkInfo = {
      current: chunkIndex + 1,
      total: totalChunks
    };

    // Extract items from this chunk
    const items = await extractQRItemsFromPDF(chunkText, chunkInfo);
    
    if (items.length === 0) {
      console.log(`No items found in chunk ${chunkIndex + 1}`);
      return [];
    }

    // Generate QR codes for these items (returns array, doesn't add to state)
    const qrCodes = await generateBulkQRCodes(items, generateQRFromPrompt, isDynamic);
    
    return qrCodes;
  };

  /**
   * Initialize chunked PDF processing
   */
  const initializeChunkedProcessing = async (pdfText, generateQRFromPrompt, isDynamic) => {
    try {
      setIsBulkProcessing(true);
      setBulkError('');
      
      // Split PDF into chunks
      const chunks = chunkPDFText(pdfText);
      
      if (chunks.length === 1) {
        // Small PDF, process normally
        console.log('PDF is small enough, processing in single chunk');
        setIsChunkedMode(false);
        const items = await extractQRItemsFromPDF(pdfText);
        setExtractedItems(items);
        const qrCodes = await generateBulkQRCodes(items, generateQRFromPrompt, isDynamic);
        return qrCodes;
      }

      // Large PDF, enable chunked mode
      console.log(`Large PDF detected. Processing in ${chunks.length} chunks`);
      setIsChunkedMode(true);
      setPdfChunks(chunks);
      setCurrentChunkIndex(0);
      setAllExtractedItems([]);

      // Process first chunk
      const firstChunkQRs = await processChunk(chunks[0], 0, chunks.length, generateQRFromPrompt, isDynamic);
      
      return firstChunkQRs;

    } catch (error) {
      console.error('Chunked processing initialization error:', error);
      setBulkError(error.message);
      throw error;
    } finally {
      setIsBulkProcessing(false);
    }
  };

  /**
   * Continue processing next chunk
   */
  const continueChunkedProcessing = async (generateQRFromPrompt, isDynamic) => {
    if (!isChunkedMode || currentChunkIndex >= pdfChunks.length - 1) {
      console.log('No more chunks to process');
      return [];
    }

    try {
      setIsBulkProcessing(true);
      setBulkError('');

      const nextChunkIndex = currentChunkIndex + 1;
      const nextChunk = pdfChunks[nextChunkIndex];

      console.log(`Continuing to chunk ${nextChunkIndex + 1}/${pdfChunks.length}`);

      // Process next chunk
      const qrCodes = await processChunk(
        nextChunk, 
        nextChunkIndex, 
        pdfChunks.length, 
        generateQRFromPrompt, 
        isDynamic
      );

      // Update state
      setCurrentChunkIndex(nextChunkIndex);

      // Check if we're done
      if (nextChunkIndex === pdfChunks.length - 1) {
        console.log('All chunks processed!');
        setIsChunkedMode(false);
      }

      return qrCodes;

    } catch (error) {
      console.error('Continue chunk error:', error);
      setBulkError(error.message);
      throw error;
    } finally {
      setIsBulkProcessing(false);
    }
  };

  /**
   * Complete PDF to QR workflow (entry point)
   */
  const processPDFToQRCodes = async (pdfText, generateQRFromPrompt, isDynamic = false) => {
    return await initializeChunkedProcessing(pdfText, generateQRFromPrompt, isDynamic);
  };

  /**
   * Get chunking status info
   */
  const getChunkingStatus = () => {
    if (!isChunkedMode) {
      return null;
    }

    return {
      currentChunk: currentChunkIndex + 1,
      totalChunks: pdfChunks.length,
      remainingChunks: pdfChunks.length - currentChunkIndex - 1,
      isComplete: currentChunkIndex >= pdfChunks.length - 1
    };
  };

  /**
   * Reset bulk generation state
   */
  const resetBulkState = () => {
    setIsBulkProcessing(false);
    setBulkProgress({ current: 0, total: 0 });
    setBulkError('');
    setExtractedItems([]);
    setPdfChunks([]);
    setCurrentChunkIndex(0);
    setAllExtractedItems([]);
    setIsChunkedMode(false);
  };

  return {
    isBulkProcessing,
    bulkProgress,
    bulkError,
    extractedItems,
    isChunkedMode,
    extractQRItemsFromPDF,
    generateBulkQRCodes,
    processPDFToQRCodes,
    continueChunkedProcessing,
    getChunkingStatus,
    resetBulkState
  };
};

export default useBulkQRGenerator;